/**
 * Link domain types and schemas
 * Defines the core Link entity with Effect Schema validation
 */

import { Schema } from "@effect/schema";
import { Data } from "effect";
import { TagResponseSchema, toTagResponse, fromTagResponse, type Tag } from "./tag.js";

/**
 * OG Metadata status
 */
export type OgMetadataStatus = "pending" | "success" | "failed";

/**
 * OG Metadata Schema
 */
export const OgMetadataSchema = Schema.Struct({
  title: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  image: Schema.NullOr(Schema.String),
  status: Schema.Literal("pending", "success", "failed"),
  fetchedAt: Schema.NullOr(Schema.String),
  error: Schema.NullOr(Schema.String),
});

/**
 * OG Metadata type
 */
export type OgMetadata = Schema.Schema.Type<typeof OgMetadataSchema>;

/**
 * Default OG metadata (pending state)
 */
export const defaultOgMetadata: OgMetadata = {
  title: null,
  description: null,
  image: null,
  status: "pending",
  fetchedAt: null,
  error: null,
};

/**
 * Tag Schema for Link embedding (simplified)
 */
export const EmbeddedTagSchema = Schema.Struct({
  id: Schema.UUID,
  name: Schema.String,
  createdAt: Schema.DateFromSelf,
});

/**
 * Link Schema using @effect/schema for runtime validation
 */
export class LinkSchema extends Schema.Class<LinkSchema>("Link")({
  id: Schema.UUID,
  url: Schema.String.pipe(Schema.pattern(/^https?:\/\/.+/)),
  title: Schema.NullOr(Schema.String),
  createdAt: Schema.DateFromSelf,
  updatedAt: Schema.DateFromSelf,
  ogMetadata: OgMetadataSchema,
  tags: Schema.Array(EmbeddedTagSchema),
}) {}

/**
 * Link type derived from schema
 */
export type Link = Schema.Schema.Type<typeof LinkSchema>;

/**
 * Link immutable data class using Effect Data
 */
export class LinkData extends Data.Class<{
  readonly id: string;
  readonly url: string;
  readonly title: string | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly ogMetadata: OgMetadata;
  readonly tags: ReadonlyArray<Tag>;
}> {
  /**
   * Creates a new Link instance with validation
   */
  static create(props: {
    readonly id: string;
    readonly url: string;
    readonly title: string | null;
    readonly createdAt: Date;
    readonly updatedAt: Date;
    readonly ogMetadata?: OgMetadata;
    readonly tags?: ReadonlyArray<Tag>;
  }): LinkData {
    return new LinkData({
      ...props,
      ogMetadata: props.ogMetadata ?? defaultOgMetadata,
      tags: props.tags ?? [],
    });
  }

  /**
   * Creates a Link from plain object with schema validation
   */
  static fromObject(obj: unknown): LinkData {
    const decoded = Schema.decodeUnknownSync(LinkSchema)(obj);
    return new LinkData({
      id: decoded.id,
      url: decoded.url,
      title: decoded.title,
      createdAt: decoded.createdAt,
      updatedAt: decoded.updatedAt,
      ogMetadata: decoded.ogMetadata,
      tags: decoded.tags,
    });
  }
}

/**
 * Create Link Request Schema
 */
export class CreateLinkRequestSchema extends Schema.Struct({
  url: Schema.String.pipe(Schema.pattern(/^https?:\/\/.+/)),
  title: Schema.optional(Schema.NullOr(Schema.String)),
  tags: Schema.optional(Schema.Array(Schema.String)),
}) {}

/**
 * Create Link Request type
 */
export type CreateLinkRequest = Schema.Schema.Type<
  typeof CreateLinkRequestSchema
>;

/**
 * Update Link Request Schema
 */
export class UpdateLinkRequestSchema extends Schema.Struct({
  title: Schema.optional(Schema.NullOr(Schema.String)),
  tags: Schema.optional(Schema.Array(Schema.String)),
}) {}

/**
 * Update Link Request type
 */
export type UpdateLinkRequest = Schema.Schema.Type<
  typeof UpdateLinkRequestSchema
>;

/**
 * OG Metadata Response Schema (for API responses)
 */
export const OgMetadataResponseSchema = Schema.Struct({
  title: Schema.NullOr(Schema.String),
  description: Schema.NullOr(Schema.String),
  image: Schema.NullOr(Schema.String),
  status: Schema.Literal("pending", "success", "failed"),
  fetchedAt: Schema.NullOr(Schema.String),
  error: Schema.NullOr(Schema.String),
});

/**
 * Link Response Schema (for API responses)
 */
export class LinkResponseSchema extends Schema.Struct({
  id: Schema.UUID,
  url: Schema.String,
  title: Schema.NullOr(Schema.String),
  createdAt: Schema.String,
  updatedAt: Schema.String,
  ogMetadata: OgMetadataResponseSchema,
  tags: Schema.Array(TagResponseSchema),
}) {}

/**
 * Link Response type
 */
export type LinkResponse = Schema.Schema.Type<typeof LinkResponseSchema>;

/**
 * Links List Response Schema
 */
export class LinksListResponseSchema extends Schema.Struct({
  links: Schema.Array(LinkResponseSchema),
  nextCursor: Schema.NullOr(Schema.String),
}) {}

/**
 * Links List Response type
 */
export type LinksListResponse = Schema.Schema.Type<
  typeof LinksListResponseSchema
>;

/**
 * Converts Link to LinkResponse (for API serialization)
 */
export const toLinkResponse = (link: Link): LinkResponse => ({
  id: link.id,
  url: link.url,
  title: link.title,
  createdAt: link.createdAt.toISOString(),
  updatedAt: link.updatedAt.toISOString(),
  ogMetadata: link.ogMetadata,
  tags: link.tags.map(toTagResponse),
});

/**
 * Converts LinkResponse to Link (for API deserialization)
 */
export const fromLinkResponse = (response: LinkResponse): Link => ({
  id: response.id,
  url: response.url,
  title: response.title,
  createdAt: new Date(response.createdAt),
  updatedAt: new Date(response.updatedAt),
  ogMetadata: response.ogMetadata,
  tags: response.tags.map(fromTagResponse),
});
